import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FiUser, FiMail, FiCalendar, FiEdit2, FiSave } from 'react-icons/fi';
import './Profile.css';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await updateProfile({ firstName, lastName });
      if (result.success) {
        setEditing(false);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="profile-header">
          <h1 className="page-title">Profile</h1>
          <p className="page-subtitle">Manage your account information</p>
        </div>

        {message.text && (
          <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
            {message.text}
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <div className="avatar-placeholder">
              <FiUser size={32} />
            </div>
            <div>
              <h2 className="card-title">{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User'}</h2>
              <p className="card-subtitle">{user.job_title || user.title || 'User'}</p>
            </div>
          </div>

          <div className="divider"></div>

          <div className="profile-details">
            <div className="detail-row">
              <div className="detail-label">
                <FiUser size={20} />
                <span>First Name</span>
              </div>
              {editing ? (
                <input
                  type="text"
                  className="form-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              ) : (
                <div className="detail-value">{user.first_name || 'Not set'}</div>
              )}
            </div>

            <div className="detail-row">
              <div className="detail-label">
                <FiUser size={20} />
                <span>Last Name</span>
              </div>
              {editing ? (
                <input
                  type="text"
                  className="form-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              ) : (
                <div className="detail-value">{user.last_name || 'Not set'}</div>
              )}
            </div>

            <div className="detail-row">
              <div className="detail-label">
                <FiMail size={20} />
                <span>Email Address</span>
              </div>
              <div className="detail-value">{user.email}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">
                <FiCalendar size={20} />
                <span>Member Since</span>
              </div>
              <div className="detail-value">
                {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'N/A'}
              </div>
            </div>
          </div>

          <div className="divider"></div>

          <div className="profile-actions">
            {editing ? (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditing(false);
                    setFirstName(user.first_name || '');
                    setLastName(user.last_name || '');
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={loading}
                >
                  <FiSave size={20} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                className="btn btn-primary"
                onClick={() => setEditing(true)}
              >
                <FiEdit2 size={20} />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="section-title">Account Information</h3>
          <div className="divider"></div>
          
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">Account ID</div>
              <div className="info-value">{user.id}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Email Verified</div>
              <div className="info-value">{user.email_verified ? 'Yes' : 'No'}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Phone</div>
              <div className="info-value">{user.phone || 'Not set'}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Status</div>
              <div className="info-value">
                <span className="status-badge status-active">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
